import {
    forwardRef, useImperativeHandle, useRef, useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { Modal, RenderIf } from 'react-rainbow-components';
import { showAppNotification } from '@rainbow-modules/app';
import { Link2, PenLine } from 'lucide-react';
import Button from 'components/Button';
import ButtonIcon from 'components/ButtonIcon';
import ModalBody from 'components/ModalBody';
import {
    InvoiceBuilderApiError,
    fetchSigningSession,
    recallSigningLink,
} from '../api/client';
import {
    signingSessionQueryKey,
    useCreateSigningSession,
    useRevokeSigningSession,
} from '../api/hooks';
import { isSigningLinkActive } from '../helpers';
import { InvoiceSigningSession } from '../types';
import {
    ButtonsContainer,
    FieldValue,
    FieldValueRow,
    HelpText,
    InfoCard,
    InfoIcon,
    InfoText,
    InfoTitle,
    ModalContent,
    StyledCopyToClipboardButton,
} from './styled';

const buttonStyles = {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    marginLeft: '0.7rem',
};

interface SigningLink {
    url: string;
    expiresAt: string;
}

interface RequestSignatureButtonProps {
    organizationId: string;
    invoiceId: string;
    size?: 'small' | 'medium';
    showTrigger?: boolean;
}

export interface RequestSignatureButtonHandle {
    open: () => void;
}

const formatExpiry = (expiresAt: string) => {
    const date = new Date(expiresAt);
    if (Number.isNaN(date.getTime())) return expiresAt;
    return new Intl.DateTimeFormat(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    }).format(date);
};

const RequestSignatureButton = forwardRef<
RequestSignatureButtonHandle,
RequestSignatureButtonProps
>(({
    organizationId,
    invoiceId,
    size = 'medium',
    showTrigger = true,
}, ref) => {
    const queryClient = useQueryClient();
    const {
        mutateAsync: createSession,
        isLoading: isCreating,
    } = useCreateSigningSession(organizationId);
    const {
        mutateAsync: revokeSession,
        isLoading: isRevoking,
    } = useRevokeSigningSession(organizationId);
    const [isOpen, setOpen] = useState(false);
    const [isChecking, setChecking] = useState(false);
    const [link, setLink] = useState<SigningLink | null>(null);
    const [isRevoked, setRevoked] = useState(false);

    const refreshSession = () => {
        queryClient.invalidateQueries(signingSessionQueryKey(organizationId, invoiceId));
    };

    const existingLink = (session: InvoiceSigningSession): SigningLink | null => {
        if (session.acceptance || !isSigningLinkActive(session.expiresAt)) return null;
        const remembered = recallSigningLink(organizationId, invoiceId);
        const url = session.url
            || (remembered && isSigningLinkActive(remembered.expiresAt) ? remembered.url : '');
        return { url, expiresAt: session.expiresAt as string };
    };

    const handleOpen = async () => {
        if (isChecking) return;
        setChecking(true);
        setRevoked(false);
        try {
            const session = await fetchSigningSession(organizationId, invoiceId);
            setLink(existingLink(session.data));
            setOpen(true);
        } catch (error) {
            if (
                error instanceof InvoiceBuilderApiError
                && (error.status === 404 || error.status === 410)
            ) {
                setLink(null);
                setOpen(true);
            } else {
                showAppNotification({
                    title: 'Could not load signing link',
                    description: error instanceof Error ? error.message : 'Please try again.',
                    icon: 'error',
                    timeout: 5000,
                });
            }
        }
        setChecking(false);
    };

    const openRequest = useRef(handleOpen);
    openRequest.current = handleOpen;
    useImperativeHandle(ref, () => ({
        open: () => {
            openRequest.current();
        },
    }), []);

    const handleCreate = async () => {
        try {
            const created = await createSession(invoiceId);
            if (!created.url || !created.expiresAt) {
                throw new Error('The signing link was not returned.');
            }
            setRevoked(false);
            setLink({ url: created.url, expiresAt: created.expiresAt });
            refreshSession();
        } catch (error: any) {
            showAppNotification({
                title: 'Could not create signing link',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
    };

    const handleRevoke = async () => {
        try {
            await revokeSession(invoiceId);
            setLink(null);
            setRevoked(true);
            refreshSession();
        } catch (error: any) {
            showAppNotification({
                title: 'Could not revoke signing link',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
    };

    return (
        <>
            {showTrigger ? (
                <ButtonIcon
                    size={size}
                    borderRadius={size === 'small' ? 'semi-rounded' : undefined}
                    icon={<PenLine size={18} />}
                    tooltip="Create signature link"
                    disabled={isChecking}
                    onClick={handleOpen}
                />
            ) : null}
            <Modal
                isOpen={isOpen}
                onRequestClose={() => setOpen(false)}
                title=""
                footer={(
                    <ButtonsContainer>
                        {link ? (
                            <>
                                <Button
                                    // eslint-disable-next-line react/forbid-component-props
                                    style={buttonStyles}
                                    label="Close"
                                    variant="border"
                                    borderRadius="semi-square"
                                    disabled={isRevoking}
                                    onClick={() => setOpen(false)}
                                />
                                <Button
                                    // eslint-disable-next-line react/forbid-component-props
                                    style={buttonStyles}
                                    label="Revoke"
                                    variant="destructive"
                                    borderRadius="semi-square"
                                    isLoading={isRevoking}
                                    onClick={handleRevoke}
                                />
                            </>
                        ) : (
                            <>
                                <Button
                                    // eslint-disable-next-line react/forbid-component-props
                                    style={buttonStyles}
                                    label="Cancel"
                                    variant="border"
                                    borderRadius="semi-square"
                                    disabled={isCreating}
                                    onClick={() => setOpen(false)}
                                />
                                <Button
                                    // eslint-disable-next-line react/forbid-component-props
                                    style={buttonStyles}
                                    label="Create link"
                                    variant="brand"
                                    borderRadius="semi-square"
                                    isLoading={isCreating}
                                    onClick={handleCreate}
                                />
                            </>
                        )}
                    </ButtonsContainer>
                )}
            >
                <ModalContent>
                    {link ? (
                        <>
                            <ModalBody
                                icon={<PenLine />}
                                title="Create signature link"
                                description="Copy this link and share it with your customer."
                            />
                            {link.url ? (
                                <FieldValueRow>
                                    <FieldValue title={link.url}>{link.url}</FieldValue>
                                    <StyledCopyToClipboardButton
                                        value={link.url}
                                        mode="icon"
                                        borderRadius="semi-square"
                                    />
                                </FieldValueRow>
                            ) : (
                                <HelpText>
                                    This link was created earlier and can no longer be shown.
                                    Revoke it if it should no longer be used.
                                </HelpText>
                            )}
                            <HelpText>
                                {`Expires ${formatExpiry(link.expiresAt)} · closes after signing.`}
                            </HelpText>
                        </>
                    ) : (
                        <>
                            <ModalBody
                                icon={<PenLine />}
                                title="Create signature link"
                                description="Create a link for your customer to review and sign this invoice. Share it yourself."
                            />
                            <InfoCard>
                                <InfoIcon>
                                    <Link2 />
                                </InfoIcon>
                                <div>
                                    <InfoTitle>Valid for 7 days</InfoTitle>
                                    <InfoText>Anyone with the link can view and sign.</InfoText>
                                </div>
                            </InfoCard>
                            <RenderIf isTrue={isRevoked}>
                                <HelpText>Signing link revoked.</HelpText>
                            </RenderIf>
                        </>
                    )}
                    <HelpText>
                        Creating a link or receiving a signature does not change payment status.
                    </HelpText>
                </ModalContent>
            </Modal>
        </>
    );
});

RequestSignatureButton.displayName = 'RequestSignatureButton';

export default RequestSignatureButton;
